<template>
  <error-boundary module="review-module">
    <hr-module class="review-module" no-header mobile-header>
      <hr-module-header
        class="__title col-xs-12 col-sm-10 col-centered"
        :title="t('review-module:title')"
      >
        <p class="__description">
          {{ t('review-module:description') }}
        </p>
      </hr-module-header>

      <h-r-module-section>
        <div
          v-for="(document, index) in state.documents"
          :key="index"
          class="__document"
        >
          <div class="__document-container">
            <div class="__document-title">
              <strong>{{ document.title }}</strong>
            </div>
            <div
              data-test-id="edit-button"
              class="__edit-button"
              @click="edit(document)"
            >
              {{ t('review-module:edit') }}
            </div>
          </div>

          <hr-review-document-info
            v-for="(field, index) in document.values"
            :key="index"
            :label="field?.label"
            :value="field?.value"
          ></hr-review-document-info>
        </div>
      </h-r-module-section>

      <template v-slot:footer>
        <hr-footer :button="t('review-module:submit')" @click="submit">
          {{ t('review-module:submit-text') }}
        </hr-footer>
      </template>
    </hr-module>
  </error-boundary>
</template>

<script>
import HrModule from '@/components/hr-module.vue';
import HrFooter from '@/components/hr-footer.vue';
import HRModuleSection from '@/components/HRModuleSection.vue';
import HrModuleHeader from '@/components/hr-module-header.vue';
import {
  status as onboardingApiStatus,
  submit as onboardingApiSubmit,
} from '@/core/svc-client/onboarding';
import { reactive } from 'vue';
import store from '@/store';
import router from '@/router';
import t from '@/lib/i18next';
import { HR_ONBOARDING_STATES } from '@/core/onboarding-states';
import { DOCUMENT_NAMES } from '@/core/field-names';
import HrReviewDocumentInfo from '@/components/hr-review-document-info.vue';
import { QUESTION_VALUE_TYPES } from '@/core/input-types/custom_questions';
import { DateTime } from 'luxon';
import { logWebHrError } from '@/core/logger/log-wrapper';
import ErrorBoundary from '@/components/error-boundary.vue';

export default {
  name: 'review-module',
  components: {
    ErrorBoundary,
    HrReviewDocumentInfo,
    HrModule,
    HrFooter,
    HRModuleSection,
    HrModuleHeader,
  },
  setup() {
    const stateModule = store.state;
    const state = reactive({
      documents: [],
    });

    async function submit() {
      try {
        await onboardingApiSubmit(stateModule.onboardingId);
        store.commit('setOnboardingStatus', HR_ONBOARDING_STATES.SUBMITTED);
        await router.push({ name: 'last-module' });
      } catch (e) {
        logWebHrError({
          message: 'Failed to submit form',
          source: 'review-module:submit',
          metadata: {
            onboardingId: stateModule.onboardingId,
          },
          error: e,
        });
        state.error = e;
      }
    }

    function edit(document) {
      store.commit('menuToggle', false);
      onboardingApiStatus(stateModule.onboardingId, 'STARTED');
      store.commit('setOnboardingStatus', HR_ONBOARDING_STATES.STARTED);

      router.push({
        name: 'document-module',
        params: {
          onboardingId: stateModule.onboardingId,
          document: JSON.stringify(document),
          slug: document.slug,
        },
      });
    }

    function joinWithCommaAnd(array) {
      switch (array.length) {
        case 0: {
          return '';
        }
        case 1: {
          return array[0];
        }
        case 2: {
          return array.join(' and \n');
        }
        default: {
          const lastElement = array.slice(-1);
          return `${array.slice(0, -1).join(',\n')} and\n${lastElement}`;
        }
      }
    }

    const handleValuePerType = {
      address: (val) => val?.formatted_address || '',
      // Some selectors have object format while other one is just pure string
      select: (val) => val?.label || val,
      selectboxes: (valObj, component) => {
        const indexedComponentValues = component.values.reduce((acc, item) => {
          acc[item.value] = item;
          return acc;
        }, {});

        const selectedValues = [];
        Object.entries(valObj).forEach(([key, value]) => {
          if (value) {
            selectedValues.push(`'${indexedComponentValues?.[key]?.label}'`);
          }
        });

        return joinWithCommaAnd(selectedValues);
      },
      // For now, we are only displaying date in MM/DD/YYYY format
      // TODO: improve unit test which enable to mock locale data for different region
      datetime: (val) =>
        val
          ? DateTime.fromISO(val)
              .setLocale('en-US')
              .toLocaleString(DateTime.DATE_SHORT)
          : t('not-provided'),
    };

    function handleValues(item, documentFields) {
      if (item.type in handleValuePerType) {
        return handleValuePerType[item.type](documentFields[item.key], item);
      } else if (
        item.decimalLimit &&
        typeof documentFields[item.key] === 'number'
      ) {
        const number = documentFields[item.key];
        const factor = 10 ** item.decimalLimit;

        // Javascript doesn't have a built-in function to round down to a specific number of decimal places
        return Math.floor(number * factor) / factor;
      } else if (typeof documentFields[item.key] === 'number') {
        return documentFields[item.key];
      } else {
        return documentFields[item.key] || '';
      }
    }

    function filterComponentsToDisplay(currentComponents, documentFields) {
      const filledComponents = currentComponents.filter(
        (item) =>
          item.key in documentFields ||
          // Why? To differentiate nested vs hidden panel components by using 'collapsed' property
          ('components' in item && item.collapsed === false)
      );

      const linearisedComponents = filledComponents.reduce(
        (acc, item) =>
          item.type === 'panel'
            ? [...acc, item, ...item.components]
            : [...acc, item],
        []
      );

      return linearisedComponents.filter(
        (item) =>
          (item.key in documentFields || item.type === 'panel') &&
          item.key !== 'submit' &&
          item.type !== 'signature'
      );
    }

    function formatDocumentFields(document) {
      const documentFields = document.formData?.data ?? {};
      const form = stateModule.indexedForms[document.formId];

      if (!form || !documentFields) {
        return [];
      }

      let componentsToFilter = form?.components || [];

      const employeePanelIndex = componentsToFilter.findIndex(
        (item) => item.key === 'employeePanel'
      );

      if (employeePanelIndex > -1) {
        componentsToFilter = form.components[employeePanelIndex].components;
      }

      const filteredComponents = filterComponentsToDisplay(
        componentsToFilter,
        documentFields
      );

      return filteredComponents.map((item) => {
        const value = handleValues(item, documentFields);

        return {
          label: item.label,
          value,
        };
      });
    }

    function constructI9TranslatorFields(indexedI9Components, documentFields) {
      let i9TranslatorFields = [];

      // Following labels are not translated because other parts of the form are not translated from formio,
      // so it is consistent to not translate these labels as well.
      // Also ideally this conversion should happen at the formio instead
      // TODO: research on formio side to render whole review screen
      const MAX_TRANSLATORS = 4;
      for (let i = 1; i <= MAX_TRANSLATORS; i++) {
        if (`preparerOrTranslatorLastName${i}` in documentFields) {
          const fullName = [
            documentFields[`preparerOrTranslatorFirstName${i}`],
            documentFields[`preparerOrTranslatorMiddleInitial${i}`],
            documentFields[`preparerOrTranslatorLastName${i}`],
          ]
            .filter((item) => !!item)
            .join(' ');

          const translatorFields = [
            {
              // This object is a separator because of the way the current grid is structured
              label: '',
              value: '',
            },
            {
              // we cannot use label as we're a hiding label for this field
              label: indexedI9Components[`signerPanel${i}`].title,
              value: '',
            },
            {
              // manual combination of multiple fields doesn't have a label
              label: 'Full name',
              value: fullName,
            },
            {
              label:
                indexedI9Components[`addressPreparerOrTranslator${i}`].label,
              value: handleValuePerType.address(
                documentFields[`addressPreparerOrTranslator${i}`]
              ),
            },
            {
              label: indexedI9Components[`signedDateTranslator${i}`].label,
              value: documentFields[`signedDateTranslator${i}`],
            },
          ];

          i9TranslatorFields = [...i9TranslatorFields, ...translatorFields];
        } else {
          break;
        }
      }

      return i9TranslatorFields;
    }

    function splitAt(predicate, arr) {
      const index = arr.findIndex(predicate);
      if (index === -1) {
        return [arr, []];
      }

      return [arr.slice(0, index), arr.slice(index)];
    }

    function mapI9Document(i9Components, documentFields) {
      const [i9EmployeeFieldComponents, i9TranslatorFieldComponents] = splitAt(
        (item) => item.key === 'signerPanel1',
        i9Components
      );

      const i9EmployeeFields = i9EmployeeFieldComponents.reduce((acc, item) => {
        const value = handleValues(item, documentFields);

        if (documentFields[item.key]) {
          acc.push({
            key: item.key,
            label: item.label,
            value,
          });
        }

        return acc;
      }, []);

      const indexedI9TranslatorFieldComponents =
        i9TranslatorFieldComponents.reduce((acc, item) => {
          acc[item.key] = item;
          return acc;
        }, {});

      const i9TranslatorFields = constructI9TranslatorFields(
        indexedI9TranslatorFieldComponents,
        documentFields
      );

      return [...i9EmployeeFields, ...i9TranslatorFields];
    }

    function formatEligibilityVerification(document) {
      const documentFields = document.formData?.data ?? {};

      const form = stateModule.indexedForms[document.formId];
      if (!form || !documentFields) {
        return [];
      }

      const filteredComponents = filterComponentsToDisplay(
        form.components.find((item) => item.key === 'employeePanel')
          ?.components,
        documentFields
      ).filter((item) => item.type !== 'checkbox');

      return mapI9Document(filteredComponents, documentFields);
    }

    function hasFullStopInAnswer(answer) {
      if (!answer) {
        return false;
      }
      return answer.slice(answer.length - 1) === '.';
    }

    function hasResponseArray(question) {
      return (
        question?.responses &&
        Array.isArray(question.responses) &&
        question.responses.length
      );
    }

    function formatQuestionValue(field) {
      const isRequiredQuestion = field?.isRequired ?? false;
      if (!hasResponseArray(field)) {
        return isRequiredQuestion ? '' : t('review-module:not-provided');
      }

      const { answer = '', comment = '' } = field?.responses[0];
      let separator =
        field.type === QUESTION_VALUE_TYPES.YESNO
          ? ', '
          : !hasFullStopInAnswer(answer)
          ? '. '
          : ' ';
      if (!answer) {
        separator = '';
      }

      let value = answer + (comment ? `${separator}${comment}` : '');
      if (!answer && !comment) {
        value = t('review-module:not-provided');
      }

      return value;
    }

    function formatAdditionalQuestionsFields(document) {
      return document.fields?.reduce((result, curr) => {
        let additionalQuestions = {};
        const isRequiredQuestion = curr?.questionValue?.isRequired ?? false;
        const requiredSuffix = isRequiredQuestion ? '*' : '';
        additionalQuestions = {
          ...additionalQuestions,
          label: curr.questionValue.question + requiredSuffix,
          value: formatQuestionValue(curr.value),
        };
        result = [...result, additionalQuestions];
        return result;
      }, []);
    }

    function formatDocSignatureFields(document) {
      return document.fields?.reduce((result, curr) => {
        let docToSign = {};
        if (curr.value?.providerData?.signed) {
          docToSign = {
            ...docToSign,
            label: curr?.label,
            value: t('review-module:completed'),
          };
          result = [...result, docToSign];
        }
        return result;
      }, []);
    }

    function formatFileUploadFields(document) {
      return document.fields?.reduce((result, curr) => {
        let fileUpload = {};

        const fileSize = parseInt(curr.fileValue?.sizeInBytes) || 0;

        let fileName = fileSize > 0 ? curr?.label : '';

        fileUpload = {
          ...fileUpload,
          label: curr?.label,
          value: fileName,
        };
        result = [...result, fileUpload];

        return result;
      }, []);
    }

    function formatDocuments(documents) {
      return documents.reduce((result, document) => {
        let formattedDocs = {};
        switch (document.name) {
          case DOCUMENT_NAMES.ADDITIONAL_QUESTIONS:
            formattedDocs = formatAdditionalQuestionsFields(document);
            break;
          case DOCUMENT_NAMES.DOC_SIGNATURE_REQUESTS:
            formattedDocs = formatDocSignatureFields(document);
            break;
          case DOCUMENT_NAMES.FILE_UPLOADS:
            formattedDocs = formatFileUploadFields(document);
            break;
          case DOCUMENT_NAMES.I9:
            formattedDocs = formatEligibilityVerification(document);
            break;
          default:
            formattedDocs = formatDocumentFields(document);
            break;
        }
        result = [
          ...result,
          {
            name: document.name,
            title: document.title,
            values: formattedDocs,
            slug: document.slug,
          },
        ];
        return result;
      }, []);
    }

    function initialize() {
      // prevent to go forward if document is not complete
      if (!store.getters.isDocumentsComplete) {
        router.push({
          name: 'OnboardingModule',
          params: { onboardingId: stateModule.onboardingId },
        });

        return;
      }
      state.documents = formatDocuments(stateModule.documents);
    }

    initialize();

    return {
      t,
      state,
      stateModule,
      submit,
      edit,
    };
  },
};
</script>

<style scoped lang="scss">
@import '~@/core/style-vars.scss';
// This is the US form related view stylings
.__title {
  margin-top: 0;
  text-align: left;
}

.review-module .hr-module-header {
  margin-bottom: 64px;

  @media only screen and (max-width: 768px) {
    margin: 0px auto 120px auto;
    padding: 0 20px;
  }
}

.__title,
.__description {
  color: var(--dpt-text-color);
}

.__document {
  margin-bottom: 12px;
  padding-bottom: 12px;
}

.__document-container {
  display: flex;
  align-items: baseline;
}

.__document-title strong {
  color: var(--dpt-text-color);
  margin-left: 5px;
  font-weight: 600;
}

.__document-title {
  flex-grow: 1;
  text-align: left;
  font-size: 1.25rem;
  margin-bottom: 12px;
  margin-top: 24px;
}

.__edit-button {
  cursor: pointer;
  color: var(--dpt-text-color-accent);
}
</style>
<style>
.review-module .HRModule__body {
  padding-bottom: 90px;
}
</style>
